import React, { useState, useMemo } from "react";
import { TEMPLATES } from "../../engine/templates";
import { w2pt } from "../../utils/coords";

const CATEGORIES = ["All", "Residential", "Commercial"];

// ─── Tiny floor-plan thumbnail renderer ──────────────────────────────────────
function MiniPlan({ state, size = 160 }) {
  if (!state) return <div style={{ width: size, height: size, background: "#06111e" }} />;

  const nodes    = Object.values(state.nodes || {});
  const edges    = Object.values(state.edges || {});
  const faces    = Object.values(state.faces || {});
  const rooms    = state.rooms || {};

  if (!nodes.length) return <div style={{ width: size, height: size, background: "#06111e" }} />;

  // Compute bounding box of all nodes
  const xs = nodes.map(n => n.x), ys = nodes.map(n => n.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const rangeX = maxX - minX || 1, rangeY = maxY - minY || 1;
  const pad = size * 0.1;
  const scale = (size - pad * 2) / Math.max(rangeX, rangeY);

  const toS = (x, y) => ({
    x: pad + (x - minX) * scale,
    y: pad + (y - minY) * scale,
  });

  const ROOM_COLORS = {
    ROOM:"#1a2030", BEDROOM:"#1a2438", LIVING:"#1c2418", KITCHEN:"#241c18",
    BATHROOM:"#181c28", TOILET:"#181c28", STORE:"#1c1c1c", CORRIDOR:"#1e1e24",
    OFFICE:"#1a2030", BALCONY:"#181e18",
  };
  const ROOM_STROKES = {
    ROOM:"#4ade80", BEDROOM:"#60a5fa", LIVING:"#a3e635", KITCHEN:"#fb923c",
    BATHROOM:"#22d3ee", TOILET:"#67e8f9", STORE:"#9ca3af", CORRIDOR:"#c4b5fd",
    OFFICE:"#f9a8d4", BALCONY:"#86efac",
  };

  return (
    <svg width={size} height={size} style={{ display: "block", borderRadius: 6 }}>
      <rect width={size} height={size} fill="#06111e" />

      {/* Faces */}
      {faces.map(f => {
        const pts = (f.loop || []).map(hid => {
          const he = state.halfEdges[hid];
          if (!he) return null;
          const n = state.nodes[he.originId];
          if (!n) return null;
          return toS(n.x, n.y);
        }).filter(Boolean);
        if (pts.length < 3) return null;
        const rd = rooms[f.id];
        const rt = rd?.type || "ROOM";
        return (
          <polygon key={f.id}
            points={pts.map(p => `${p.x},${p.y}`).join(" ")}
            fill={ROOM_COLORS[rt] || "#1a2030"}
            stroke={ROOM_STROKES[rt] || "#4ade80"}
            strokeWidth={0.6}
            fillOpacity={0.8}
          />
        );
      })}

      {/* Walls */}
      {edges.map(e => {
        const ab = state.halfEdges[e.halfEdgeA], ba = state.halfEdges[e.halfEdgeB];
        if (!ab || !ba) return null;
        const nA = state.nodes[ab.originId], nB = state.nodes[ba.originId];
        if (!nA || !nB) return null;
        const pA = toS(nA.x, nA.y), pB = toS(nB.x, nB.y);
        const mat = e.materialId || "BRICK";
        const color = mat === "GLASS" ? "#88ccff" : mat === "PARTITION" ? "#c0c0a0" : "#3b82f6";
        return (
          <line key={e.id}
            x1={pA.x} y1={pA.y} x2={pB.x} y2={pB.y}
            stroke={color} strokeWidth={mat === "BRICK" || mat === "RC" ? 2 : 1}
            opacity={0.9}
          />
        );
      })}
    </svg>
  );
}

// ─── Template card ────────────────────────────────────────────────────────────
function TemplateCard({ tmpl, selected, onSelect, previewState }) {
  return (
    <button onClick={() => onSelect(tmpl)}
      style={{
        background: selected ? "#0c1c38" : "#08111e",
        border: `1px solid ${selected ? "#3b82f660" : "#131924"}`,
        borderRadius: 12, padding: "14px",
        cursor: "pointer", textAlign: "left",
        transition: "border-color .15s, background .15s",
        display: "flex", flexDirection: "column", gap: 10,
      }}
      onMouseOver={e => { if (!selected) e.currentTarget.style.borderColor = "#1e3d66"; }}
      onMouseOut={e  => { if (!selected) e.currentTarget.style.borderColor = "#131924"; }}
    >
      <MiniPlan state={previewState} size={148} />
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: 16 }}>{tmpl.icon}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: selected ? "#dde6f0" : "#8496a8", fontFamily: "'JetBrains Mono',monospace" }}>{tmpl.name}</span>
        </div>
        <div style={{ fontSize: 11, color: "#3d5068", lineHeight: 1.5, marginBottom: 6 }}>{tmpl.desc}</div>
        <div style={{ display: "flex", gap: 6 }}>
          <span style={{ fontSize: 9, color: "#2a3a4a", background: "#0c1320", border: "1px solid #131924", padding: "2px 7px", borderRadius: 10, fontFamily: "'JetBrains Mono',monospace" }}>
            {tmpl.category}
          </span>
          <span style={{ fontSize: 9, color: "#2a3a4a", background: "#0c1320", border: "1px solid #131924", padding: "2px 7px", borderRadius: 10, fontFamily: "'JetBrains Mono',monospace" }}>
            ~{tmpl.area} m²
          </span>
        </div>
      </div>
    </button>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
export default function TemplateModal({ onApply, onClose }) {
  const [category, setCategory] = useState("All");
  const [selected, setSelected] = useState(null);

  // Pre-build all template states once
  const previewStates = useMemo(() => {
    const map = {};
    for (const t of TEMPLATES) {
      try { map[t.id] = t.fn(); } catch (e) { map[t.id] = null; }
    }
    return map;
  }, []);

  const filtered = category === "All" ? TEMPLATES : TEMPLATES.filter(t => t.category === category);

  const handleApply = () => {
    if (!selected) return;
    const state = previewStates[selected.id];
    if (state) onApply(state);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000c", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(6px)" }}>
      <div style={{ width: "min(92vw,960px)", maxHeight: "90vh", background: "#08111e", border: "1px solid #1e3d6640", borderRadius: 16, display: "flex", flexDirection: "column", boxShadow: "0 32px 80px #000e" }}>

        {/* Header */}
        <div style={{ padding: "20px 24px 0", borderBottom: "1px solid #131924", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 9, color: "#3b82f6", letterSpacing: 2.5, fontWeight: 800, marginBottom: 6, fontFamily: "'JetBrains Mono',monospace" }}>TEMPLATES</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: "#dde6f0", margin: 0, fontFamily: "'Syne',sans-serif" }}>Start from a template</h2>
              <p style={{ fontSize: 12, color: "#3d5068", margin: "4px 0 0" }}>Choose a layout — you can edit everything after loading.</p>
            </div>
            <button onClick={onClose} style={{ background: "transparent", border: "1px solid #131924", color: "#4a5e70", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
              ✕
            </button>
          </div>

          {/* Category tabs */}
          <div style={{ display: "flex", gap: 4, marginBottom: -1 }}>
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)}
                style={{
                  padding: "7px 16px", background: "transparent", border: "none",
                  borderBottom: `2px solid ${category === c ? "#3b82f6" : "transparent"}`,
                  color: category === c ? "#60a5fa" : "#3d5068",
                  cursor: "pointer", fontSize: 12, fontWeight: category === c ? 700 : 400,
                  fontFamily: "inherit", transition: "color .15s",
                }}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(176px,1fr))", gap: 12 }}>
            {filtered.map(t => (
              <TemplateCard key={t.id} tmpl={t}
                selected={selected?.id === t.id}
                onSelect={setSelected}
                previewState={previewStates[t.id]}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #131924", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: "#2a3a4a" }}>
            {selected
              ? `${selected.icon} ${selected.name} selected — all walls, rooms, and dimensions are fully editable`
              : "Select a template to preview"}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={{ padding: "9px 18px", background: "transparent", border: "1px solid #131924", color: "#4a5e70", borderRadius: 7, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
              Start blank
            </button>
            <button onClick={handleApply} disabled={!selected}
              style={{ padding: "9px 24px", background: selected ? "linear-gradient(90deg,#3b82f6,#6366f1)" : "#0c1320", border: "none", color: selected ? "#fff" : "#2a3a4a", borderRadius: 7, cursor: selected ? "pointer" : "default", fontSize: 13, fontWeight: 700, fontFamily: "inherit", transition: "background .2s" }}>
              Load template →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
